import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import CelularMaskInput from '../masks/CelularMaskInput';
import { formatCelular, unformatCelular } from '../../utils/celularUtils';
import { formatTelefone, unformatTelefone } from '../../utils/telefoneUtils';
import TelefoneMaskInput from '../masks/TelefoneMaskInput';
import CEPMaskInput from '../masks/CEPMaskInput';
import { formatCEP, unformatCEP } from '../../utils/cepUtils';
import DefaultModal from './DefaultModal';
import CNPJMaskInput from '../masks/CNPJMaskInput';
import { formatCNPJ, isValidCNPJ, unformatCNPJ } from '../../utils/cnpjUtils';

function limparTelefone(telefone) {
  return telefone ? telefone.replace(/\D/g, '').slice(0, 10) : '';
}

function limparCelular(celular) {
  return celular ? celular.replace(/\D/g, '').slice(0, 11) : '';
}

function AssociacaoModal({
  show,
  onClose,
  onSaveSuccess,
  associacao = null,
  isEditing = false,
}) {
  const [formData, setFormData] = useState({
    cnpj: '',
    nome: '',
    nomeFantasia: '',
    email: '',
    telefone: '',
    celular: '',
    logrNome: '',
    logrNum: '',
    logrCompl: '',
    logrBairro: '',
    logrCep: '',
    logrUf: '',
    logrCidade: '',
  });

  const [UFs, setUFs] = useState([]);
  const [cidades, setCidades] = useState([]);
  const token = localStorage.getItem('token');
  const [modalData, setModalData] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [cnpjError, setCnpjError] = useState('');
  const [isBuscandoCnpj, setIsBuscandoCnpj] = useState(false);
  const [cidadesKey, setCidadesKey] = useState(0);
  const refs = useRef({});

  const showDefaultModal = (type, message) => {
    setModalData({ show: true, type, message });
  };

  const closeDefaultModal = () => {
    setModalData(prevModalData => ({ ...prevModalData, show: false }));
  };

  useEffect(() => {
    if (show) {
      // Reset modalData quando o AssociacaoModal é aberto
      setModalData({
        show: false,
        type: '',
        message: '',
      });

      if (isEditing && associacao) {
        setFormData({
          cnpj: associacao.cnpj || '',
          nome: associacao.nome || '',
          nomeFantasia: associacao.nomeFantasia || '',
          email: associacao.email || '',
          telefone: associacao.telefone || '',
          celular: associacao.celular || '',
          logrNome: associacao.logrNome || '',
          logrNum: associacao.logrNum || '',
          logrCompl: associacao.logrCompl || '',
          logrBairro: associacao.logrBairro || '',
          logrCep: associacao.logrCep || '',
          logrUf: associacao.logrUf || '',
          logrCidade: associacao.logrCidade || '',
          transparenciaPublica: Boolean(associacao.transparenciaPublica),
        });
      } else {
        setFormData({
          cnpj: '',
          nome: '',
          nomeFantasia: '',
          email: '',
          telefone: '',
          celular: '',
          logrNome: '',
          logrNum: '',
          logrCompl: '',
          logrBairro: '',
          logrCep: '',
          logrUf: '',
          logrCidade: '',
          transparenciaPublica: false,
        });
      }
      setCnpjError('');

      axios
        .get(`${API_BASE_URL.UF}/all`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(result => {
          setUFs(result.data);
        })
        .catch(error => {
          console.error('Erro ao obter listas:', error);
        });
    }
  }, [show, isEditing, associacao, token]);

  useEffect(() => {
    if (formData.logrUf) {
      axios
        .get(`${API_BASE_URL.MUNICIPIO}/uf/${formData.logrUf}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(result => {
          setCidades(result.data);
          if (formData.logrCidade) {
            const norm = s =>
              s
                ? s
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .toUpperCase()
                : '';
            const match = result.data.find(
              c => norm(c.nome) === norm(formData.logrCidade)
            );
            if (match && match.nome !== formData.logrCidade) {
              setFormData(f => ({ ...f, logrCidade: match.nome }));
            }
          }
        })
        .catch(error => {
          console.error('Erro ao carregar cidades:', error);
        });
    } else {
      setCidades([]);
    }
  }, [formData.logrUf, cidadesKey, token]);

  function stripMask(value) {
    return value ? value.replace(/\D/g, '') : '';
  }

  async function handleBuscarCnpj() {
    const cnpjNum = stripMask(formData.cnpj);
    if (cnpjNum.length !== 14) {
      showDefaultModal('error', 'Digite um CNPJ válido antes de buscar.');
      return;
    }
    setIsBuscandoCnpj(true);
    try {
      const r = await axios.get(`${API_BASE_URL.CNPJ_PROXY}/${cnpjNum}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = typeof r.data === 'string' ? JSON.parse(r.data) : r.data;
      if (d.status === 'ERROR') {
        showDefaultModal('error', d.message || 'CNPJ não encontrado.');
        return;
      }
      setFormData(f => ({
        ...f,
        nome: d.nome || f.nome,
        email: d.email || f.email,
        telefone: d.telefone || f.telefone,
        logrNome: d.logradouro || f.logrNome,
        logrNum: d.numero || f.logrNum,
        logrCompl: d.complemento || f.logrCompl,
        logrBairro: d.bairro || f.logrBairro,
        logrCep: (d.cep || '').replace(/\D/g, '') || f.logrCep,
        logrUf: d.uf || f.logrUf,
        logrCidade: d.municipio || f.logrCidade,
      }));
      setCidadesKey(k => k + 1);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        showDefaultModal(
          'error',
          'Limite de consultas atingido. Aguarde 1 minuto e tente novamente.'
        );
      } else if (status === 400) {
        showDefaultModal('error', 'CNPJ inválido.');
      } else {
        showDefaultModal(
          'error',
          `Erro ao consultar CNPJ${status ? ` (${status})` : ''}. Tente novamente em instantes.`
        );
      }
    } finally {
      setIsBuscandoCnpj(false);
    }
  }

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const unformattedAssociacao = {
      ...formData,
      cnpj: unformatCNPJ(formData.cnpj),
      telefone: limparTelefone(formData.telefone),
      celular: limparCelular(formData.celular),
    };

    if (!isValidCNPJ(unformattedAssociacao.cnpj)) {
      setCnpjError('CNPJ inválido');
      return;
    }

    setCnpjError('');

    axios
      .post(`${API_BASE_URL.ASSOCIACAO}/save`, unformattedAssociacao, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        showDefaultModal('success', 'Associação cadastrada com sucesso!');
        setTimeout(() => {
          closeDefaultModal();
          onSaveSuccess && onSaveSuccess(result.data);
          onClose && onClose();
        }, 1500);
      })
      .catch(error => {
        console.error('Erro ao salvar associação:', error);
        showDefaultModal(
          'error',
          'Erro ao salvar associação. Verifique os dados e tente novamente.'
        );
      });
  };

  if (!show) return null;

  return (
    <>
      <div
        className="modal show d-block"
        tabIndex="-1"
        style={{
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1055,
          position: 'fixed',
          inset: 0,
        }}
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div
              className="modal-header bg-success text-white"
              style={{ height: '30px' }}
            >
              <h5 className="modal-title mb-0">
                <i className="bi bi-diagram-2 me-2"></i>
                {isEditing ? 'EDITAR ASSOCIAÇÃO' : 'NOVA ASSOCIAÇÃO'}
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row g-2">
                  {/* CNPJ com botão de busca */}
                  <div className="col-12 col-sm-4">
                    <label className="form-label mb-1">CNPJ *</label>
                    <div className="input-group input-group-sm">
                      <CNPJMaskInput
                        value={formatCNPJ(formData.cnpj)}
                        onChange={e =>
                          handleChange({
                            target: {
                              name: 'cnpj',
                              value: unformatCNPJ(e.target.value),
                            },
                          })
                        }
                        name="cnpj"
                        ref={el => (refs.current.cnpj = el)}
                        type="text"
                        required
                        disabled={isEditing}
                        className={`form-control form-control-sm ${
                          cnpjError ? 'is-invalid' : ''
                        }`}
                      />
                      
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={handleBuscarCnpj}
                          disabled={isBuscandoCnpj}
                          title="Buscar dados na Receita Federal"
                        >
                          {isBuscandoCnpj ? (
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          ) : (
                            <i className="bi bi-search" />
                          )}
                        </button>
                    
                    </div>
                    {cnpjError && (
                      <small className="text-danger d-block">{cnpjError}</small>
                    )}
                  </div>

                  {/* Nome */}
                  <div className="col-12 col-sm-8">
                    <label className="form-label mb-1">Nome *</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {/* Nome Fantasia */}
                  <div className="col-12">
                    <label className="form-label mb-1">Nome Fantasia</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="nomeFantasia"
                      value={formData.nomeFantasia}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Email */}
                  <div className="col-12 col-sm-6">
                    <label className="form-label mb-1">E-mail</label>
                    <input
                      type="email"
                      className="form-control form-control-sm"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Telefone */}
                  <div className="col-12 col-sm-3">
                    <label className="form-label mb-1">Telefone</label>
                    <div className="input-group input-group-sm">
                      <TelefoneMaskInput
                        value={formatTelefone(formData.telefone)}
                        onChange={e =>
                          handleChange({
                            target: {
                              name: 'telefone',
                              value: unformatTelefone(e.target.value),
                            },
                          })
                        }
                        name="telefone"
                        ref={el => (refs.current.telefone = el)}
                        type="text"
                        className="form-control form-control-sm"
                      />
                    </div>
                  </div>

                  {/* Celular */}
                  <div className="col-12 col-sm-3">
                    <label className="form-label mb-1">Celular</label>
                    <div className="input-group input-group-sm">
                      <CelularMaskInput
                        value={formatCelular(formData.celular)}
                        onChange={e =>
                          handleChange({
                            target: {
                              name: 'celular',
                              value: unformatCelular(e.target.value),
                            },
                          })
                        }
                        name="celular"
                        ref={el => (refs.current.celular = el)}
                        type="text"
                        className="form-control form-control-sm"
                      />
                    </div>
                  </div>

                  {/* Logradouro */}
                  <div className="col-12 col-sm-6">
                    <label className="form-label mb-1">Endereço</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="logrNome"
                      value={formData.logrNome}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Número */}
                  <div className="col-12 col-sm-2">
                    <label className="form-label mb-1">Número</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="logrNum"
                      value={formData.logrNum}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Complemento */}
                  <div className="col-12 col-sm-4">
                    <label className="form-label mb-1">Complemento</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="logrCompl"
                      value={formData.logrCompl}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Bairro */}
                  <div className="col-12 col-sm-4">
                    <label className="form-label mb-1">Bairro</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="logrBairro"
                      value={formData.logrBairro}
                      onChange={handleChange}
                    />
                  </div>

                  {/* CEP */}
                  <div className="col-12 col-sm-3">
                    <label className="form-label mb-1">CEP</label>
                    <div className="input-group input-group-sm">
                      <CEPMaskInput
                        value={formatCEP(formData.logrCep)}
                        onChange={e =>
                          handleChange({
                            target: {
                              name: 'logrCep',
                              value: unformatCEP(e.target.value),
                            },
                          })
                        }
                        name="logrCep"
                        ref={el => (refs.current.cep = el)}
                        type="text"
                        className="form-control form-control-sm"
                      />
                    </div>
                  </div>

                  {/* UF */}
                  <div className="col-md-2">
                    <label className="form-label mb-1">UF</label>
                    <select
                      className="form-select form-select-sm"
                      value={formData.logrUf}
                      onChange={handleChange}
                      name="logrUf"
                    >
                      <option value="">Selecione...</option>
                      {UFs.map((uf, index) => (
                        <option key={index} value={uf.sigla}>
                          {uf.sigla}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Cidade */}
                  <div className="col-md-4">
                    <label className="form-label mb-1">Cidade</label>
                    <select
                      className="form-select form-select-sm"
                      value={formData.logrCidade}
                      onChange={handleChange}
                      name="logrCidade"
                    >
                      <option value="">Selecione...</option>
                      {cidades.map((cidade, index) => (
                        <option key={index} value={cidade.nome}>
                          {cidade.nome}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Transparência pública */}
                  <div className="col-12">
                    <div className="form-check form-switch mt-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="chkTransparenciaPublica"
                        name="transparenciaPublica"
                        checked={formData.transparenciaPublica}
                        onChange={e =>
                          handleChange({
                            target: {
                              name: 'transparenciaPublica',
                              value: e.target.checked,
                            },
                          })
                        }
                      />
                      <label className="form-check-label" htmlFor="chkTransparenciaPublica">
                        <i className="bi bi-bank me-1"></i>
                        Exibir no Portal da Transparência
                        <small className="d-block text-muted">
                          Permite que qualquer pessoa consulte receitas, rendimentos e despesas agregadas desta associação.
                        </small>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2 mt-3">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={onClose}
                  >
                    <i className="bi bi-x-lg me-1"></i>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-success btn-sm">
                    <i className="bi bi-check-lg me-1"></i>
                    Salvar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <DefaultModal
        show={modalData.show}
        type={modalData.type}
        onClose={closeDefaultModal}
        message={modalData.message}
      />
    </>
  );
}

export default AssociacaoModal;
